import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import { updateTenant, getTenants } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import {
  Building2,
  Save,
  AlertCircle,
  HelpCircle,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";
import { useLocationContext } from "../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";

// Tooltip component (reusable)
const Tooltip = ({
  children,
  content,
}: {
  children: React.ReactNode;
  content: string;
}) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

const TENANT_CATEGORIES = [
  "Space Tourism",
  "Aerospace",
  "Retail",
  "Food & Beverage",
  "Services",
  "Technology",
  "Entertainment",
  "Other",
];

const TENANT_STATUSES = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "PENDING", label: "Pending" },
  { value: "SUSPENDED", label: "Suspended" },
];

interface FormData {
  companyName: string;
  description: string;
  category: string;
  unitNumber: string;
  floor: string;
  building: string;
  phone: string;
  email: string;
  website: string;
  contactPerson: string;
  businessHours: string;
  services: string;
  status: "ACTIVE" | "INACTIVE" | "PENDING" | "SUSPENDED";
  isVerified: boolean;
  isFeatured: boolean;
}

interface FormErrors {
  companyName?: string;
  email?: string;
  website?: string;
  submit?: string;
}

export default function EditTenantPage() {
  useRequireSectionVisible("showTenantBusiness");
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { selectedLocation, navigateToLocationPage } = useLocationContext();

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Fetch all tenants to find the one we're editing
  const { data: tenants = [], isLoading: tenantsLoading } = useQuery(
    getTenants,
    {
      filters: {
        locationId: selectedLocation?.id || undefined,
      },
    },
  );

  // Find the tenant we're editing
  const tenant = tenants.find((t) => t.id === tenantId);

  // Find current tenant index and navigation targets
  const currentIndex = tenants.findIndex((t) => t.id === tenantId);
  const previousTenant = currentIndex > 0 ? tenants[currentIndex - 1] : null;
  const nextTenant =
    currentIndex < tenants.length - 1 ? tenants[currentIndex + 1] : null;

  const [formData, setFormData] = useState<FormData>({
    companyName: "",
    description: "",
    category: "",
    unitNumber: "",
    floor: "",
    building: "",
    phone: "",
    email: "",
    website: "",
    contactPerson: "",
    businessHours: "",
    services: "",
    status: "ACTIVE",
    isVerified: false,
    isFeatured: false,
  });

  // Populate form when tenant data is loaded
  useEffect(() => {
    if (tenant) {
      setFormData({
        companyName: tenant.companyName || "",
        description: tenant.description || "",
        category: tenant.category || "",
        unitNumber: tenant.unitNumber || "",
        floor: tenant.floor || "",
        building: tenant.building || "",
        phone: tenant.phone || "",
        email: tenant.email || "",
        website: tenant.website || "",
        contactPerson: tenant.contactPerson || "",
        businessHours: tenant.businessHours || "",
        services: Array.isArray(tenant.services)
          ? tenant.services.join(", ")
          : "",
        status: tenant.status,
        isVerified: tenant.isVerified,
        isFeatured: tenant.isFeatured,
      });
    }
  }, [tenant]);

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website =
        "Please enter a valid website URL (starting with http:// or https://)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;
    if (!tenantId) return;

    setIsLoading(true);

    try {
      const updateData = {
        ...formData,
        services: formData.services
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };

      await updateTenant({
        tenantId,
        data: updateData,
      });

      // Navigate back to tenants list
      handleGoBack();
    } catch (error: any) {
      console.error("Error updating tenant:", error);
      setErrors({
        submit: error.message || "Failed to update tenant. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigateToLocationPage("/tenants");
    } else {
      navigate("/tenants");
    }
  };

  // Show loading state
  if (tenantsLoading) {
    return (
      <IndividualEditPageLayout title="Loading...">
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading tenant...</p>
          </div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  // Show error if tenant not found
  if (!tenant) {
    return (
      <IndividualEditPageLayout title="Tenant Not Found">
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <Building2 className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Tenant not found
            </h3>
            <p className="text-gray-600 mb-4">
              The tenant you're looking for doesn't exist or has been deleted.
            </p>
            <button
              onClick={handleGoBack}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Tenants
            </button>
          </div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  // Show unauthorized message if not logged in
  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied">
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Access Denied
            </h3>
            <p className="text-gray-600">
              You must be logged in to edit tenants.
            </p>
          </div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="tenant-form"
        disabled={isLoading || !formData.companyName.trim()}
        icon={Save}
      >
        {isLoading ? "Updating..." : "Update"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleGoBack} disabled={isLoading}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <>
      <SidebarSection title="Navigation" hideOnMobile>
        <div className="flex gap-2">
          <button
            onClick={() =>
              previousTenant && navigate(`/tenants/${previousTenant.id}/edit`)
            }
            disabled={!previousTenant}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              previousTenant
                ? "border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                : "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
            }`}
            title={
              previousTenant
                ? `Previous: ${previousTenant.companyName}`
                : "No previous tenant"
            }
          >
            <ChevronUp className="w-4 h-4" />
            <span className="text-sm font-medium">Previous</span>
          </button>
          <button
            onClick={() =>
              nextTenant && navigate(`/tenants/${nextTenant.id}/edit`)
            }
            disabled={!nextTenant}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              nextTenant
                ? "border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                : "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
            }`}
            title={
              nextTenant ? `Next: ${nextTenant.companyName}` : "No next tenant"
            }
          >
            <span className="text-sm font-medium">Next</span>
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      </SidebarSection>

      <SidebarSection title="Contact Information" hideOnMobile>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Contact Person
            </label>
            <input
              type="text"
              value={formData.contactPerson}
              onChange={(e) => handleChange("contactPerson", e.target.value)}
              className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
              placeholder="Contact person name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                errors.email
                  ? "border-destructive bg-destructive/10"
                  : "border-input"
              }`}
              placeholder="contact@company.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.email}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Phone (Optional)
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
              placeholder="+1 (555) 123-4567"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Website
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => handleChange("website", e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                errors.website
                  ? "border-destructive bg-destructive/10"
                  : "border-input"
              }`}
              placeholder="https://company.com"
            />
            {errors.website && (
              <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.website}
              </p>
            )}
          </div>
        </div>
      </SidebarSection>

      <SidebarSection title="Additional Options" hideOnMobile>
        <div className="space-y-3">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isVerified}
              onChange={(e) => handleChange("isVerified", e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <div className="flex items-center gap-2">
              <div className="font-medium text-gray-900">Verified Tenant</div>
              <Tooltip content="Mark this tenant as verified">
                <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
              </Tooltip>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isFeatured}
              onChange={(e) => handleChange("isFeatured", e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <div className="flex items-center gap-2">
              <div className="font-medium text-gray-900">Featured Tenant</div>
              <Tooltip content="Feature this tenant prominently">
                <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
              </Tooltip>
            </div>
          </label>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout
      title="Edit Tenant"
      subtitle={`Location: ${selectedLocation?.name || "No location selected"}`}
      icon={Building2}
      actions={actionsContent}
      sidebar={sidebarContent}
    >

      <form id="tenant-form" onSubmit={handleSubmit}>
        {/* Full-width sections */}
        <div className="space-y-6 mb-6">
          {/* Primary Input Field */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Company Information
            </h3>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) => handleChange("companyName", e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                errors.companyName
                  ? "border-destructive bg-destructive/10"
                  : "border-input"
              }`}
              placeholder="e.g., SpaceX Mars Operations, Virgin Galactic Tours"
              required
            />
            {errors.companyName && (
              <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.companyName}
              </p>
            )}
          </div>

          {/* Description Field */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Description
            </h3>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
              placeholder="Provide detailed description, services offered, any unique features or special information..."
            />
            <p className="text-sm text-gray-500 mt-2">
              {formData.description.length}/500 characters
            </p>
          </div>
        </div>

        {/* Two-column layout for form fields */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Category and Status Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleChange("category", e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                >
                  <option value="">Select a category</option>
                  {TENANT_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    handleChange("status", e.target.value as any)
                  }
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                >
                  {TENANT_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Location Details */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Specific Location
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Unit #
                  </label>
                  <input
                    type="text"
                    value={formData.unitNumber}
                    onChange={(e) => handleChange("unitNumber", e.target.value)}
                    className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                    placeholder="Unit #"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Floor
                  </label>
                  <input
                    type="text"
                    value={formData.floor}
                    onChange={(e) => handleChange("floor", e.target.value)}
                    className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                    placeholder="Floor"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Building
                  </label>
                  <input
                    type="text"
                    value={formData.building}
                    onChange={(e) => handleChange("building", e.target.value)}
                    className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                    placeholder="Building"
                  />
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Business Hours
              </h3>
              <input
                type="text"
                value={formData.businessHours}
                onChange={(e) => handleChange("businessHours", e.target.value)}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="e.g., Mon-Fri 9AM-6PM, Sat 10AM-4PM"
              />
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Services */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Services & Offerings
              </h3>
              <textarea
                value={formData.services}
                onChange={(e) => handleChange("services", e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="List main services (e.g., Space Tourism, Mars Exploration, Commercial Space Travel)"
              />
            </div>
          </div>
        </div>

        {/* Error message */}
        {errors.submit && (
          <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}
      </form>
    </IndividualEditPageLayout>
  );
}
